/**
 * R19 (2026-08-18 Miles): the wallet ledger reads task by task. A task's
 * background charges — the memory update and the follow-up-suggestions
 * distillation — are real debits with their own ledger rows (row existence is
 * the billing idempotency marker, so the rows must stay), but the wallet
 * DISPLAY folds them into their parent task row: one row per task, its amount
 * the task's full charge, in exact parity with the activity page (whose
 * server-side fold does the same for activity events).
 *
 * Linkage: aux rows and their parent turn row carry the same server-written
 * turnId; memory rows can also derive the parent id mechanically
 * (memext_{sid}_{tid} → turn_{sid}_{tid}). An aux row whose parent is not in
 * the loaded window passes through unfolded — an honest standalone row beats
 * a silently vanished charge.
 */

import type { Expense } from '../../types/expenses';

const MEMORY_PREFIX = 'memext_';
const FOLLOWUP_PREFIX = 'threadfup_';
const PARENT_PREFIX = 'turn_';

export function isAuxExpenseId(id: string | null | undefined): boolean {
  return typeof id === 'string' && (id.startsWith(MEMORY_PREFIX) || id.startsWith(FOLLOWUP_PREFIX));
}

/** Parent turn expense id derivable from a memory aux id, or null. */
export function auxParentExpenseId(id: string | null | undefined): string | null {
  if (typeof id !== 'string' || !id.startsWith(MEMORY_PREFIX)) return null;
  const rest = id.slice(MEMORY_PREFIX.length);
  return rest ? `${PARENT_PREFIX}${rest}` : null;
}

export function foldAuxExpenses(expenses: Expense[]): Expense[] {
  if (!Array.isArray(expenses) || expenses.length === 0) return expenses;

  const byId = new Map<string, Expense>();
  const parentIdByTurnId = new Map<string, string>();
  for (const e of expenses) {
    if (!e || typeof e.id !== 'string') continue;
    byId.set(e.id, e);
    if (e.id.startsWith(PARENT_PREFIX) && e.turnId) {
      parentIdByTurnId.set(e.turnId, e.id);
    }
  }

  const folded = new Map<string, Expense>();
  const dropped = new Set<string>();
  for (const e of expenses) {
    if (!e || !isAuxExpenseId(e.id)) continue;
    const parentId =
      (e.turnId && parentIdByTurnId.get(e.turnId)) || auxParentExpenseId(e.id) || null;
    const parent = parentId ? folded.get(parentId) ?? byId.get(parentId) : undefined;
    if (!parent || !parentId) continue;
    folded.set(parentId, {
      ...parent,
      amountUsd: (parent.amountUsd || 0) + (e.amountUsd || 0),
      amountOlv: (parent.amountOlv || 0) + (e.amountOlv || 0),
      // The aux call ran after the task's own steps — it joins the end of
      // the per-call breakdown the row expansion shows.
      ...(e.generations && e.generations.length > 0
        ? { generations: [...(parent.generations ?? []), ...e.generations] }
        : {}),
    });
    dropped.add(e.id);
  }
  if (dropped.size === 0) return expenses;

  return expenses
    .filter((e) => !dropped.has(e.id))
    .map((e) => folded.get(e.id) ?? e);
}
